import React, { useMemo, useState } from 'react';
import { LuInbox, LuMessageSquareText, LuSearchX } from 'react-icons/lu';
import { useVisitTopicsData } from '../../../hooks/useVisitTopicsData.js';
import { Card, CardHeader } from '../../ui/Card';
import { SearchInput } from '../../ui/SearchInput';
import SegmentedControl from '../../ui/SegmentedControl';
import Alert from '../../ui/Alert';
import Badge from '../../ui/Badge';
import EmptyState from '../../ui/EmptyState';
import Skeleton from '../../ui/Skeleton';
import PostDetail from './PostDetail';
import PostItem from './PostItem';
import TopicFilter from './TopicFilter';
import { POST_FILTERS, filterPosts } from './postList.js';

const ClientPostsView = ({ fetchWithAuth, clientSlug, selectedKey, onSelect }) => {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [topic, setTopic] = useState(null);
  const postsState = useVisitTopicsData(fetchWithAuth, `/posts?client=${encodeURIComponent(clientSlug)}`);
  const detailState = useVisitTopicsData(fetchWithAuth, selectedKey ? `/posts/${encodeURIComponent(selectedKey)}` : null);

  const posts = useMemo(() => (postsState.data ?? []).map((p) => ({ ...p, topics: p.topics ?? [] })), [postsState.data]);
  const detail = useMemo(() => detailState.data && { ...detailState.data, pictures: detailState.data.pictures ?? [], rows: detailState.data.rows ?? [] }, [detailState.data]);
  const postsReady = postsState.status === 'ready';

  const visible = useMemo(() => filterPosts(posts, { filter, topic, query }), [posts, filter, topic, query]);

  const index = visible.findIndex((p) => p.visit_key === selectedKey);
  const step = (offset) => (index >= 0 && visible[index + offset] ? () => onSelect(visible[index + offset].visit_key) : null);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <SearchInput value={query} onChange={setQuery} placeholder="Texte, canal, topic, visit_key…" className="min-w-[14rem] flex-1" />
        {postsReady && <TopicFilter posts={posts} value={topic} onChange={setTopic} />}
        <SegmentedControl size="sm" value={filter} onChange={setFilter} options={POST_FILTERS} ariaLabel="Filtrer les posts" />
      </div>
      {postsState.status === 'error' && <Alert tone="danger">Impossible de charger les posts de ce client. Veuillez réessayer.</Alert>}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:items-start">
        <Card className="overflow-hidden">
          <CardHeader icon={LuInbox} title="Posts traités" badge={postsReady && <Badge size="sm" tone="slate">{visible.length}/{posts.length}</Badge>} />
          {postsState.status === 'error' ? null : !postsReady ? (
            <div className="space-y-3 p-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}</div>
          ) : visible.length ? (
            <ul className="divide-y divide-gray-100 overflow-y-auto dark:divide-gray-900 lg:max-h-[calc(100vh-18rem)]">
              {visible.map((post) => <PostItem key={post.visit_key} post={post} active={post.visit_key === selectedKey} onSelect={onSelect} />)}
            </ul>
          ) : (
            <EmptyState icon={LuSearchX} title="Aucun post" description="Aucun post ne correspond à ces filtres." />
          )}
        </Card>

        <div className="min-w-0">
          {!selectedKey ? (
            <EmptyState bordered icon={LuMessageSquareText} title="Choisissez un post" description="Le texte du post, ses photos et ses lignes visit_topics, visite par visite." />
          ) : detailState.status === 'error' ? (
            <Alert tone="danger">Impossible de charger ce post.</Alert>
          ) : !detail ? (
            <div className="space-y-4"><Skeleton className="h-48" /><Skeleton className="h-64" /></div>
          ) : (
            <PostDetail
              key={detail.visit_key}
              fetchWithAuth={fetchWithAuth}
              post={detail}
              position={index >= 0 ? `${index + 1}/${visible.length}` : null}
              onPrevious={step(-1)}
              onNext={step(1)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientPostsView;
